// ContactPage — Phase 16 Gate 3 + Gate 4 — public access-request form.

import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { publicSite } from "@/api/endpoints";
import { serverDetail } from "@/api/client";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const COUNTRIES = [
    ["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"],
    ["AU", "Australia"], ["NZ", "New Zealand"], ["DE", "Germany"],
    ["FR", "France"], ["IT", "Italy"], ["NL", "Netherlands"],
    ["NO", "Norway"], ["SE", "Sweden"], ["FI", "Finland"], ["DK", "Denmark"],
    ["PL", "Poland"], ["JP", "Japan"], ["KR", "South Korea"],
    ["IL", "Israel"], ["IN", "India"], ["SG", "Singapore"], ["AE", "United Arab Emirates"],
    ["BR", "Brazil"], ["MX", "Mexico"], ["ZA", "South Africa"], ["TR", "Turkey"],
    ["GR", "Greece"], ["ES", "Spain"], ["PT", "Portugal"], ["CZ", "Czechia"],
    ["RO", "Romania"], ["UA", "Ukraine"], ["EE", "Estonia"], ["LV", "Latvia"],
    ["LT", "Lithuania"],
];

const INITIAL = {
    full_name: "",
    email: "",
    organisation: "",
    role_title: "",
    country: "",
    use_case: "",
    compliance_attested: false,
    tos_accepted: false,
    aup_accepted: false,
    training_mirror_consent: false,
};

export default function ContactPage() {
    const [form, setForm] = useState(INITIAL);
    const [blocked, setBlocked] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        publicSite.blockedCountries()
            .then((r) => setBlocked(r.blocked || []))
            .catch(() => setBlocked([]));
    }, []);

    function update(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        if (submitting) return;
        if (!form.country) {
            setErr("Please select a country.");
            return;
        }
        if (blocked.includes(form.country)) {
            setErr("We cannot accept access requests from this country at this time.");
            return;
        }
        setSubmitting(true);
        setErr(null);
        try {
            await publicSite.submitAccessRequest(form);
            setSuccess(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (apiErr) {
            setErr(serverDetail(apiErr));
        } finally {
            setSubmitting(false);
        }
    }

    if (success) {
        return (
            <>
                <SeoHead
                    path="/contact"
                    schemaJsonLd={[
                        buildBreadcrumbListSchema({ path: "/contact", label: "Contact" }),
                    ]}
                />
            <div data-testid="contact-success">
                <section className="section" style={{ paddingTop: 80 }}>
                    <div className="section__inner" style={{ maxWidth: 720, textAlign: "center" }}>
                        <div className="cap-card__icon" style={{ margin: "0 auto 24px", width: 56, height: 56, borderRadius: "50%" }}>
                            <CheckCircle2 size={28} />
                        </div>
                        <span className="section__eyebrow">// REQUEST RECEIVED</span>
                        <h1 className="section__heading">Thanks — your request is queued for review.</h1>
                        <p className="section__lede" style={{ margin: "0 auto 32px" }}>
                            A superadmin reviews every submission. Approved applicants
                            receive a single-use invitation link by email — usually
                            within five business days. We do not send promotional mail.
                        </p>
                        <Link to="/" className="btn-primary" data-testid="contact-success-home">
                            Return home
                        </Link>
                    </div>
                </section>
            </div>
            </>
        );
    }

    return (
        <>
            <SeoHead
                path="/contact"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/contact", label: "Contact" }),
                ]}
            />
        <div data-testid="contact-page">
            <section className="section" style={{ paddingTop: 64 }}>
                <div className="section__inner">
                    <span className="section__eyebrow">// REQUEST ACCESS</span>
                    <h1 className="section__heading">Bring us your hardest scenario.</h1>
                    <p className="section__lede" style={{ marginBottom: 36 }}>
                        UAVSimNet onboards defence, government, and licensed-industry
                        teams. Tell us who you are and what you are trying to validate.
                        We review every submission within five business days. Approved
                        applicants receive a single-use invite link to set up their
                        tenant — pricing and term are agreed during onboarding.
                    </p>
                    {/* Phase 10 — R3c: sibling links before the form so operators
                        who arrive here from a search result can self-serve into the
                        evidence / features / security reads. */}
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 12,
                            marginBottom: 28,
                        }}
                        data-testid="contact-related"
                    >
                        <Link to="/features" className="btn-ghost" data-testid="contact-link-features">
                            Counter-UAS simulation features
                        </Link>
                        <Link to="/security" className="btn-ghost" data-testid="contact-link-security">
                            Security posture
                        </Link>
                        <Link to="/use-cases" className="btn-ghost" data-testid="contact-link-use-cases">
                            Use cases
                        </Link>
                        <Link to="/evidence" className="btn-ghost" data-testid="contact-link-evidence">
                            Credibility framework
                        </Link>
                    </div>
                    {/* Phase 10 — R4c: parent-organisation attribution. */}
                    <p
                        style={{
                            color: "var(--ux-text-3)",
                            fontSize: 13,
                            marginBottom: 24,
                            maxWidth: 720,
                        }}
                        data-testid="contact-rlk-note"
                    >
                        UAVSimNet is operated by{" "}
                        <a
                            href="https://therlkgroup.com"
                            target="_blank"
                            rel="noopener"
                            data-testid="contact-rlk-link"
                            style={{ color: "var(--ux-text-2)" }}
                        >
                            The RLK Group
                        </a>
                        , a UK-registered defence engineering consultancy. For group-level
                        enquiries outside this platform, contact RLK Group directly.
                    </p>
                    {err && (
                        <div className="banner banner--danger" style={{ maxWidth: 720, marginBottom: 24 }} data-testid="contact-error">
                            {err}
                        </div>
                    )}
                    <form className="form-card" onSubmit={onSubmit} data-testid="contact-form">
                        <div className="form-grid">
                            <div className="form-row">
                                <label htmlFor="full_name">Full name</label>
                                <input
                                    id="full_name"
                                    required
                                    minLength={2}
                                    maxLength={120}
                                    value={form.full_name}
                                    onChange={(e) => update("full_name", e.target.value)}
                                    data-testid="contact-input-full-name"
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="email">Work email</label>
                                <input
                                    id="email"
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    data-testid="contact-input-email"
                                />
                            </div>
                        </div>
                        <div className="form-grid">
                            <div className="form-row">
                                <label htmlFor="organisation">Organisation</label>
                                <input
                                    id="organisation"
                                    required
                                    maxLength={160}
                                    value={form.organisation}
                                    onChange={(e) => update("organisation", e.target.value)}
                                    data-testid="contact-input-organisation"
                                />
                            </div>
                            <div className="form-row">
                                <label htmlFor="role_title">Role / title</label>
                                <input
                                    id="role_title"
                                    required
                                    maxLength={120}
                                    value={form.role_title}
                                    onChange={(e) => update("role_title", e.target.value)}
                                    data-testid="contact-input-role-title"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <label htmlFor="country">Country</label>
                            <select
                                id="country"
                                required
                                value={form.country}
                                onChange={(e) => update("country", e.target.value)}
                                data-testid="contact-input-country"
                            >
                                <option value="">— Select a country —</option>
                                {COUNTRIES
                                    .filter(([code]) => !blocked.includes(code))
                                    .map(([code, name]) => (
                                        <option key={code} value={code}>{name}</option>
                                    ))}
                            </select>
                            <span className="form-row__hint">
                                Embargoed jurisdictions are not eligible for access at any tier.
                            </span>
                        </div>
                        <div className="form-row">
                            <label htmlFor="use_case">Use case description</label>
                            <textarea
                                id="use_case"
                                required
                                minLength={20}
                                maxLength={2000}
                                value={form.use_case}
                                onChange={(e) => update("use_case", e.target.value)}
                                data-testid="contact-input-use-case"
                            />
                            <span className="form-row__hint">
                                Briefly describe the threat profile, deployment context, and timeline. The more concrete, the faster onboarding.
                            </span>
                        </div>

                        <div className="consent-block" data-testid="contact-consent-block">
                            <div className="consent-block__title">Compliance attestation</div>
                            <div className="consent-line">
                                <input
                                    type="checkbox"
                                    id="compliance_attested"
                                    checked={form.compliance_attested}
                                    onChange={(e) => update("compliance_attested", e.target.checked)}
                                    data-testid="contact-consent-compliance"
                                />
                                <label htmlFor="compliance_attested">
                                    I confirm that I am acting on behalf of a defense, government,
                                    or licensed-industry organisation, and that my use of UAVSimNet
                                    will comply with all applicable export-control regulations
                                    (ITAR / EAR / national equivalents).
                                </label>
                            </div>
                            <div className="consent-line">
                                <input
                                    type="checkbox"
                                    id="tos_accepted"
                                    checked={form.tos_accepted}
                                    onChange={(e) => update("tos_accepted", e.target.checked)}
                                    data-testid="contact-consent-tos"
                                />
                                <label htmlFor="tos_accepted">
                                    I have read and accept the <Link to="/terms">Terms of Service</Link>.
                                </label>
                            </div>
                            <div className="consent-line">
                                <input
                                    type="checkbox"
                                    id="aup_accepted"
                                    checked={form.aup_accepted}
                                    onChange={(e) => update("aup_accepted", e.target.checked)}
                                    data-testid="contact-consent-aup"
                                />
                                <label htmlFor="aup_accepted">
                                    I have read and accept the <Link to="/aup">Acceptable Use Policy</Link>.
                                </label>
                            </div>
                            <div className="consent-line">
                                <input
                                    type="checkbox"
                                    id="training_mirror_consent"
                                    checked={form.training_mirror_consent}
                                    onChange={(e) => update("training_mirror_consent", e.target.checked)}
                                    data-testid="contact-consent-training-mirror"
                                />
                                <label htmlFor="training_mirror_consent">
                                    I consent to UAVSimNet retaining anonymised,
                                    aggregated artefacts from my runs to improve future
                                    product releases. Personal identifiers are not
                                    retained. See <Link to="/privacy">Privacy</Link>{" "}
                                    for details.
                                </label>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary"
                            style={{ width: "100%", justifyContent: "center" }}
                            data-testid="contact-submit"
                        >
                            {submitting ? "Submitting…" : "Submit request"}
                        </button>
                    </form>
                    <p
                        style={{
                            color: "var(--ux-text-3)",
                            fontSize: 13,
                            marginTop: 18,
                            maxWidth: 720,
                        }}
                        data-testid="contact-privacy-note"
                    >
                        We handle the information you submit under our{" "}
                        <Link to="/privacy" style={{ color: "var(--ux-accent)" }}>
                            privacy policy
                        </Link>
                        . Compliance and consent timestamps are stored alongside your
                        record for audit retrieval.
                    </p>
                </div>
            </section>
        </div>
        </>
    );
}
